<template>
    <div class="pin-input" :class="{'error': isError, 'pin-input--dark': darkColor}">
        <span :id="groupLabelId" class="visually-hidden">{{ groupLabelText }}</span>
        <div
            class="pin-input__row"
            role="group"
            :aria-labelledby="groupLabelId"
            :aria-invalid="isError || undefined"
            :aria-describedby="isError ? errorId : undefined"
            @click="onRowClick"
        >
            <input
                v-for="(item, index) in pinInputs"
                :key="index"
                v-model="pinInputs[index]"
                :ref="(el) => setInputRef(el, index)"
                :type="masked ? 'password' : 'tel'"
                inputmode="numeric"
                maxlength="1"
                pattern="\d"
                class="pin-input__input"
                placeholder=" "
                :name="`${name}-${index + 1}`"
                :aria-label="`${groupLabelText}, цифра ${index + 1} из ${length}`"
                :aria-invalid="isError || undefined"
                :aria-describedby="isError ? errorId : undefined"
                :autocomplete="masked ? 'off' : (index === 0 ? 'one-time-code' : 'off')"
                @keydown="onKeydown(index, $event)"
                @paste="onPaste($event)"
                @input="onInput(index, $event)"
                @keyup.enter.prevent="emit('enter')"
                @focus="onFocus(index, $event)"
            />
        </div>
        <div :id="errorId" class="pin-input__error" role="alert" aria-live="assertive">{{ isError ? errorMsg : '' }}</div>
    </div>
</template>

<script lang="ts" setup>
import {useUniqueId} from '~/composables/useUniqueId';

const props = defineProps({
    errorMsg: {
        type: String,
        default: ''
    },
    darkColor: {
        type: Boolean,
        default: false
    },
    length: {
        type: Number,
        default: 6
    },
    name: {
        type: String,
        default: 'code'
    },
    groupLabel: {
        type: String,
        default: ''
    },
    masked: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['enter']);

const modelValue = defineModel<string>({required: true});
const pinInputs = ref<Array<string>>(Array(props.length).fill(''));
const inputs = ref<(HTMLInputElement | null)[]>([]);
const groupLabelId = useUniqueId('pin-group-label');
const errorId = useUniqueId('pin-error');

const isError = computed(() => props.errorMsg.length > 0);
const isAutofocused = ref(false);
const groupLabelText = computed(() =>
    props.groupLabel || (props.masked ? 'ПИН-код' : 'Код подтверждения'));

const setInputRef = (el: Element | null, index: number) => {
    if (el instanceof HTMLInputElement) {
        inputs.value[index] = el;
    }
};

onBeforeUpdate(() => {
    inputs.value = [];
});

const getInput = (index: number): HTMLInputElement | null => inputs.value[index] ?? null;

const onKeydown = (index: number, event: KeyboardEvent) => {
    if (event.key === 'Backspace' && !pinInputs.value[index] && index !== 0) {
        isAutofocused.value = true;
        getInput(index - 1)?.focus();
    }
};

const onPaste = (event: ClipboardEvent) => {
    const paste = event.clipboardData?.getData('text') ?? '';
    const pasteArray = paste.split('').filter((char: string) => /\d/.test(char));
    if (pasteArray.length) {
        const fillCount = Math.min(pasteArray.length, props.length);
        for (let i = 0; i < fillCount; i++) {
            pinInputs.value[i] = pasteArray[i];
        }
        updateValue();
        isAutofocused.value = true;
        const focusIndex = pasteArray.length < props.length ? pasteArray.length : props.length - 1;
        getInput(focusIndex)?.focus();
    } else {
        updateValue();
    }
    event.preventDefault();
};

const onInput = async (index: number, event: Event) => {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    if (value === '') {
        pinInputs.value[index] = '';
        updateValue();
    } else {
        if (value.length === 1) {
            pinInputs.value[index] = value.replace(/\D/g, '');
            await updateValue();
            if (pinInputs.value[index] === '') return;
            isAutofocused.value = true;
            if (index < pinInputs.value.length - 1) {
                getInput(index + 1)?.focus();
            }
        } else {
            pinInputs.value[index] = value.slice(-1);
            updateValue();
        }
    }
};

const onFocus = (index?: number, event?: FocusEvent) => {
    if (isAutofocused.value) {
        isAutofocused.value = false;
        return;
    }
    if (index !== undefined && modelValue.value.length + 1 === index + 1) return;
    const row = (event?.target as HTMLElement | undefined)?.parentElement;
    if (!row) return;

    if (modelValue.value.length === 0) {
        const firstInput = row.querySelector('input');
        if (firstInput instanceof HTMLInputElement) {
            isAutofocused.value = true;
            firstInput.focus();
        }
    } else {
        let nextIndex = modelValue.value.length;
        if (nextIndex === props.length) nextIndex = props.length - 1;
        const nextInput = row.querySelectorAll('input')[nextIndex];
        if (nextInput instanceof HTMLInputElement) {
            isAutofocused.value = true;
            nextInput.focus();
        }
    }
};

const updateValue = () => {
    modelValue.value = pinInputs.value.join('');
};

const clearValue = async () => {
    pinInputs.value = Array(props.length).fill('');
    modelValue.value = '';
    await nextTick();
    focusFirst();
};

const focusFirst = () => {
    const first = getInput(0);
    if (!first) return;
    isAutofocused.value = true;
    first.focus();
};

const onRowClick = (event: MouseEvent) => {
    if ((event.target as HTMLElement).tagName === 'INPUT') return;
    const firstEmpty = pinInputs.value.findIndex((v) => !v);
    const index = firstEmpty === -1 ? props.length - 1 : firstEmpty;
    const input = getInput(index);
    if (!input) return;
    isAutofocused.value = true;
    input.focus();
};

defineExpose({clearValue, focusFirst});
</script>

<style lang="scss">
.pin-input {
    &__row {
        display: flex;
        justify-content: center;
        background: $color-dark-white;
        border: 1px solid $color-dark-white;
        border-radius: 12px;
        overflow: hidden;
        cursor: text;

        &:focus-within {
            border-color: $color-purple;
        }
    }
    input {
        width: 32px;
        height: 48px;
        border-radius: 0;
        border: none;
        padding: 0;
        text-align: center;
        font-size: 14px;
        box-sizing: border-box;
        background-color: $color-dark-white;
        font-weight: 500;
        -moz-appearance: textfield;

        &:focus {
            outline: none;
        }
        &:placeholder-shown {
            background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOCIgaGVpZ2h0PSI4IiB2aWV3Qm94PSIwIDAgOCA4IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8Y2lyY2xlIGN4PSI0IiBjeT0iNCIgcj0iNCIgZmlsbD0iIzY5NzA3NyIvPgo8L3N2Zz4K);
            background-repeat: no-repeat;
            background-position: center;
            @include dark {
              background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOCIgaGVpZ2h0PSI4IiB2aWV3Qm94PSIwIDAgOCA4IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8Y2lyY2xlIGN4PSI0IiBjeT0iNCIgcj0iNCIgZmlsbD0iI0E4QTBCNiIvPgo8L3N2Zz4K);
            }
        }
        &::placeholder {
            color: $color-dark-white;
        }

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active  {
            box-shadow: 0 0 0 30px $color-dark-white inset !important;
        }

        @include media(sm) {
            padding: 15px 2px;
        }

    }

    &--dark &__row {
        background-color: $color-dark-white;
    }

    &.error &__row {
        border-color: $color-red;
    }

    &__error {
        margin-top: 8px;
        color: $color-red;
        font-size: 12px;
    }
}
</style>
